'use client';

import {
	Close as CancelIcon,
	DeleteOutlined as DeleteIcon,
	Edit as EditIcon,
	Save as SaveIcon,
} from '@mui/icons-material';
import { Box, Button, Checkbox, CircularProgress, Dialog, FormControlLabel, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useMutation } from '@parkchirp/api-client';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CommonContext } from '../../../contexts';
import { REMOVE_PARKER_RESERVED_SPACE, UPDATE_PARKER_RESERVED_SPACE } from '../../../mutations/reservedSpaces';
import { SetShowSnackbar } from '../../../types';
import { Dayjs, dayjs, isAdminDashboard, isOwnerPortal, isReservedSpaceRangeReserved } from '../../../utils';
import { AssignReservedSpaceModal, StagedReservedSpaceAssignment } from './assignReservedSpaceModal';

interface ReservedSpaceParkerProps {
	parkerId: string;
	sublotId: string;
	terminationStatus: string;
	parkerLoaded: boolean;
	setShowSnackbar: SetShowSnackbar;
	isWholeModalEditMode: boolean;
	isNewParker?: boolean;
	pendingReservedSpaceAssignment?: StagedReservedSpaceAssignment[] | null;
	onStageReservedSpaceAssignment?: (assignment: StagedReservedSpaceAssignment[] | null) => void;
	reservedSpaces: {
		id: string;
		reservedSpaceId: number;
		spaceNumber: string;
		level: string;
		startDate: string;
		endDate: string | null;
	}[];
	onReservedSpaceAssigned?: () => void;
}

const formatInventoryDate = (date?: string | null) => {
	if (!date) {
		return '';
	}

	return dayjs.utc(date).format('MM/DD/YYYY');
};

export const ReservedSpaceParkerTable = ({
	parkerId,
	sublotId,
	terminationStatus,
	parkerLoaded,
	setShowSnackbar,
	isWholeModalEditMode,
	isNewParker,
	pendingReservedSpaceAssignment,
	onStageReservedSpaceAssignment,
	reservedSpaces,
	onReservedSpaceAssigned,
}: ReservedSpaceParkerProps) => {
	const rows = useMemo(() => {
		if (isNewParker) {
			return pendingReservedSpaceAssignment
				? pendingReservedSpaceAssignment.map((assignment) => ({
						id: assignment.id,
						reservedSpaceId: assignment.reservedSpaceId,
						spaceNumber: assignment.spaceNumber,
						level: assignment.level,
						startDate: assignment.startDate,
						endDate: assignment.endDate,
					}))
				: [];
		}

		return reservedSpaces || [];
	}, [isNewParker, pendingReservedSpaceAssignment, reservedSpaces]);
	const loading = !parkerLoaded;
	const { hasComponentAccess } = useContext(CommonContext);
	const parkerTerminated = terminationStatus === 'TERMINATED';
	const [showAssignReservedSpaceModal, setShowAssignReservedSpaceModal] = useState<boolean>(false);
	const [editRowId, setEditRowId] = useState<string | number | null>(null);
	const [editReservedSpaceId, setEditReservedSpaceId] = useState<number | null>(null);
	const [editStartDate, setEditStartDate] = useState<string>('');
	const [editEndDate, setEditEndDate] = useState<string | null>(null);
	const [editNoEndDate, setEditNoEndDate] = useState<boolean>(true);
	const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);

	const handleAssignSpaceClick = useCallback(() => {
		setShowAssignReservedSpaceModal(true);
	}, []);

	const closeModal = useCallback(() => {
		setShowAssignReservedSpaceModal(false);
	}, []);

	const resetEditState = useCallback(() => {
		setEditRowId(null);
		setEditReservedSpaceId(null);
		setEditStartDate('');
		setEditEndDate(null);
		setEditNoEndDate(true);
	}, []);

	const canEditStartDate = useCallback((row: { startDate: string }) => {
		return dayjs.utc(row.startDate).isAfter(dayjs.utc(), 'day');
	}, []);

	const handleEditClick = useCallback(
		(row: { id: string | number; reservedSpaceId: number; startDate: string; endDate: string | null }) => {
			setEditRowId(row.id);
			setEditReservedSpaceId(row.reservedSpaceId);
			setEditStartDate(row.startDate);
			setEditEndDate(row.endDate || null);
			setEditNoEndDate(!row.endDate);
		},
		[],
	);

	const isRangeReserved = useCallback(
		(rangeStart: Dayjs, rangeEnd: Dayjs | null) => {
			return isReservedSpaceRangeReserved(reservedSpaces, rangeStart, rangeEnd, editRowId);
		},
		[reservedSpaces, editRowId],
	);

	const isStartDateReserved = useCallback(
		(date: Dayjs) => {
			const day = date.startOf('day');
			return isRangeReserved(day, day);
		},
		[isRangeReserved],
	);

	const isEndDateReserved = useCallback(
		(date: Dayjs, row: { startDate: string }) => {
			const effectiveStartDateSource = canEditStartDate(row) ? editStartDate : row.startDate;
			const effectiveStartDate = dayjs.utc(effectiveStartDateSource).startOf('day');
			return isRangeReserved(effectiveStartDate, date.startOf('day'));
		},
		[isRangeReserved, canEditStartDate, editStartDate],
	);

	const [updateParkerReservedSpace] = useMutation(UPDATE_PARKER_RESERVED_SPACE, {
		onCompleted: () => {
			setShowSnackbar({ message: 'Reserved space updated successfully', severity: 'success' });
			resetEditState();
			onReservedSpaceAssigned?.();
			setIsSavingEdit(false);
		},
		onError: (error) => {
			setShowSnackbar({ message: error.message, severity: 'error' });
			setIsSavingEdit(false);
		},
		context: { apiVersion: 'v2', appName: process.env.NEXT_PUBLIC_APP_NAME },
	});

	const handleSaveEditClick = useCallback(() => {
		if (editRowId === null) {
			return;
		}
		setIsSavingEdit(true);

		if (isNewParker) {
			const editingRow = rows.find((row) => row.id === editRowId);
			if (editingRow && pendingReservedSpaceAssignment) {
				const updatedAssignments = pendingReservedSpaceAssignment.map((assignment) =>
					assignment.reservedSpaceId === editingRow.reservedSpaceId
						? { ...assignment, startDate: editStartDate, endDate: editEndDate || null }
						: assignment,
				);
				onStageReservedSpaceAssignment?.(updatedAssignments);
			}
			resetEditState();
			setIsSavingEdit(false);
			return;
		}

		updateParkerReservedSpace({
			variables: {
				input: {
					id: editRowId,
					parkerId,
					reservedSpaceId: editReservedSpaceId,
					startDate: editStartDate,
					endDate: editEndDate || null,
				},
			},
		});
	}, [
		editRowId,
		editReservedSpaceId,
		isNewParker,
		rows,
		pendingReservedSpaceAssignment,
		onStageReservedSpaceAssignment,
		resetEditState,
		updateParkerReservedSpace,
		parkerId,
		editStartDate,
		editEndDate,
	]);

	const [removeParkerReservedSpace] = useMutation(REMOVE_PARKER_RESERVED_SPACE, {
		onCompleted: () => {
			setShowSnackbar({ message: 'Reserved space removed successfully', severity: 'success' });
			onReservedSpaceAssigned?.();
		},
		onError: (error) => {
			setShowSnackbar({ message: error.message, severity: 'error' });
		},
		context: { apiVersion: 'v2', appName: process.env.NEXT_PUBLIC_APP_NAME },
	});

	const handleDeleteClick = useCallback(
		(id: string) => {
			if (isNewParker) {
				const updatedAssignments =
					pendingReservedSpaceAssignment?.filter((assignment) => assignment.id !== id) || [];
				onStageReservedSpaceAssignment?.(updatedAssignments.length > 0 ? updatedAssignments : null);
			} else {
				removeParkerReservedSpace({
					variables: {
						input: {
							id,
							parkerId,
						},
					},
				});
			}
		},
		[
			isNewParker,
			onStageReservedSpaceAssignment,
			removeParkerReservedSpace,
			parkerId,
			pendingReservedSpaceAssignment,
		],
	);

	const columns: GridColDef[] = [
		{
			field: 'level',
			headerName: 'Level',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 1,
			sortable: false,
			resizable: false,
		},
		{
			field: 'spaceNumber',
			headerName: 'Space Number',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 1,
			sortable: false,
			resizable: false,
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 1,
			sortable: false,
			resizable: false,
			renderCell: (params) => {
				if (editRowId !== params.row.id || !canEditStartDate(params.row)) {
					return formatInventoryDate(params.row.startDate);
				}

				return (
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker<Dayjs>
							label='Start Date'
							value={dayjs.utc(editStartDate)}
							disablePast
							shouldDisableDate={isStartDateReserved}
							onChange={(value) => {
								setEditStartDate(value ? value.format() : editStartDate);
								if (value.isSameOrAfter(editEndDate)) {
									setEditEndDate(null);
								}
							}}
							sx={{ width: '100%', outlineStyle: 'none' }}
							slotProps={{
								textField: {
									size: 'small',
									variant: 'standard',
									inputProps: {
										'data-cy': 'reservedSpace-edit-startDate',
										disabled: true,
									},
								},
							}}
						/>
					</LocalizationProvider>
				);
			},
		},
		{
			field: 'endDate',
			headerName: 'End Date',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 1,
			sortable: false,
			resizable: false,
			renderCell: (params) => {
				if (editRowId !== params.row.id) {
					return formatInventoryDate(params.row.endDate);
				}

				const minEndDateSource = canEditStartDate(params.row) ? editStartDate : params.row.startDate;
				const minEndDate = dayjs.utc(minEndDateSource).add(1, 'day').startOf('day');
				const inactiveDateReference =
					dayjs(params.row.startDate).add(1, 'day').startOf('day') ??
					dayjs(editStartDate).add(1, 'day').startOf('day');

				return (
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box sx={{ width: '100%' }}>
							<DatePicker<Dayjs>
								label='End Date'
								value={editEndDate ? dayjs.utc(editEndDate) : null}
								minDate={minEndDate}
								referenceDate={inactiveDateReference}
								shouldDisableDate={(date) => isEndDateReserved(date, params.row)}
								onChange={(value) => {
									setEditEndDate(value ? value.format() : null);
									setEditNoEndDate(!value);
								}}
								sx={{ width: '100%', outlineStyle: 'none' }}
								slotProps={{
									textField: {
										size: 'small',
										variant: 'standard',
										inputProps: {
											'data-cy': 'reservedSpace-edit-endDate',
											disabled: true,
										},
									},
								}}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={editNoEndDate}
										onChange={(e) => {
											setEditNoEndDate(e.target.checked);
											if (e.target.checked) {
												setEditEndDate(null);
											}
										}}
										onClick={(e) => e.stopPropagation()}
										inputProps={
											{
												'data-cy': 'reservedSpace-edit-noEndDate',
											} as React.InputHTMLAttributes<HTMLInputElement>
										}
									/>
								}
								label='No end date'
								sx={{ m: 0 }}
							/>
						</Box>
					</LocalizationProvider>
				);
			},
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 140,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				if (isWholeModalEditMode && !isNewParker) {
					return null;
				}

				if (editRowId === params.row.id) {
					return (
						<>
							<IconButton onClick={handleSaveEditClick} data-cy='reservedSpace-save-button'>
								{isSavingEdit ? <CircularProgress size={25} sx={{ color: 'white' }} /> : <SaveIcon />}
							</IconButton>
							<IconButton onClick={resetEditState} data-cy='reservedSpace-cancel-button'>
								<CancelIcon />
							</IconButton>
						</>
					);
				}

				return (
					<>
						<IconButton onClick={() => handleEditClick(params.row)}>
							<EditIcon />
						</IconButton>
						<IconButton onClick={() => handleDeleteClick(params.row.id)}>
							<DeleteIcon />
						</IconButton>
					</>
				);
			},
		},
	];

	return (
		<Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
			<h2>Reserved Space</h2>
			<Dialog open={showAssignReservedSpaceModal}>
				<Grid item p={3}>
					<AssignReservedSpaceModal
						parkerId={parkerId}
						sublotId={sublotId}
						reservedSpaces={reservedSpaces}
						closeModal={closeModal}
						setShowSnackbar={setShowSnackbar}
						onAssigned={onReservedSpaceAssigned}
						isNewParker={isNewParker}
						pendingReservedSpaceAssignment={pendingReservedSpaceAssignment}
						onStageAssignment={isNewParker ? onStageReservedSpaceAssignment : undefined}
					/>
				</Grid>
			</Dialog>
			<DataGridPremium
				sx={{ mt: 2, mb: 2 }}
				rows={rows}
				columns={columns}
				loading={loading}
				autoHeight
				getRowHeight={() => 'auto'}
				disableRowSelectionOnClick
				disableColumnReorder
				disableColumnMenu
				hideFooter
			/>
			{!isOwnerPortal &&
				(!isWholeModalEditMode || isNewParker) &&
				(!isAdminDashboard || hasComponentAccess('ParkerDetailModalAssignReservedSpaceButton')) && (
					<Grid pt={2} item xs={12}>
						<Button
							variant='contained'
							onClick={handleAssignSpaceClick}
							size='small'
							disabled={!sublotId || parkerTerminated}
							data-cy='schedule-reserved-space-change-button'
						>
							Assign Reserved Space
						</Button>
						{!sublotId && (
							<span style={{ marginLeft: '10px', color: 'red' }}>
								Select a sublot to assign a reserved space.
							</span>
						)}
					</Grid>
				)}
		</Grid>
	);
};
